import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, TableContainer, TableBody, TableFooter, Paper } from '@mui/material';
import { tableContainerStyle } from 'components/tables/common';
import PayoutInfoPopup from 'components/payoutInfoPopup';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useFilterValues } from 'hooks/useFilterContext';
import { useStoreAuditSummaries } from 'features/audits/hooks/queries/getStoreAuditSummaries';
import { useStoresSummaryStoresOnly } from 'features/Stores';
import {
  getStoreReconciliationAuditsByDate,
  type AuditReportPackScopeMetric,
} from 'services/auditService';
import AuditTableHeader from './AuditTableHeader';  
import AuditTableRow from './AuditTableRow';
import AuditTableTotalRow from './AuditTableTotalRow';
import AuditTableEmpty from './AuditTableEmpty';
import { AuditRow, AuditTableProps } from '../../types';
import TableLoading from '../../../../components/tables/common/TableLoading';
// import ReconciliationPopup from 'components/ReconciliationPopup';
import { useAuditsCompleteThroughDate, useStoreBackstockLedger } from '../../hooks';
import type { BackstockLedgerRow } from '../../types';
import { buildCurrentInventoryStatusPath } from 'features/audits/utils';
import { getAuditTableColStyles } from './auditTableColumnStyles';

// Column configurations for different store types
const COLUMN_CONFIGS = {
  BothConnected: [
    'Opening Tickets', 'Activated Tickets', 'Closing Tickets', 'Active in Backstock',
    'Expected Sales', 'POS Sales', 'Variance',
    'Lottery Reported Sales', 'POS Lotto Sales', 'Variance', 'Lottery Reported Payouts', 'Lottery Reported Free Tickets',
    'POS Payouts', 'Variance'
  ],
  BothNotConnected: [
    'Opening Tickets', 'Activated Tickets', 'Closing Tickets', 'Active in Backstock',
    'Expected Sales', 'Expected Sales', 'Scratcher Qty', 'Scratcher Sales'
  ],
  LottoConnectedOnly: [
    'Opening Tickets', 'Activated Tickets', 'Closing Tickets', 'Active in Backstock',
    'Expected Sales', 'Expected Sales', 'Scratcher Qty', 'Scratcher Sales',
    'Lottery Reported Sales', 'Lottery Reported Payouts'
  ],
  POSConnectedOnly: [
    'Opening Tickets', 'Activated Tickets', 'Closing Tickets', 'Active in Backstock',
    'Expected Sales', 'POS Sales', 'Variance',
    'Lottery Reported Sales', 'POS Lotto Sales', 'Variance', 'Lottery Reported Payouts', 'Lottery Reported Free Tickets',
    'POS Payouts', 'Variance'
  ]
} as const;

const EXPAND_ALL_AUDIT_DETAILS_CONCURRENCY = 3;
const STOCK_COLUMN_LABELS = new Set(['Opening Tickets', 'Activated Tickets', 'Closing Tickets']);
const COUNTER_MOVEMENT_STOCK_COLUMN_COUNT = 6;
const BACKSTOCK_ACCOUNTING_COLUMNS = [
  'Opening',
  'Activated to Backstock',
  'Counter to Backstock',
  'Backstock to Counter',
  'Returned to Lottery',
  'Sold Out',
  'Closing',
] as const;

type ExpandableAuditRow = Pick<AuditRow, 'report_date'>;

type BackstockPackMetric =
  | 'opening'
  | 'activated_to_backstock'
  | 'moved_from_counter'
  | 'returned_to_counter'
  | 'returned_to_lottery'
  | 'inventory_depletion'
  | 'closing';

const getAuditRowAuditCount = (row?: Partial<AuditRow> | null) => {
  return Number(row?.audit_summary?.audit_count ?? row?.audits?.length ?? 0);
};

const getAuditStoreKey = (storeId: string | undefined) => storeId || 'store';

const getAuditRowExpandKey = (storeId: string | undefined, reportDate?: string | null) => {
  return `${getAuditStoreKey(storeId)}-${reportDate || 'no-date'}`;
};

const AuditTable = React.memo<AuditTableProps>(({
  handleAuditDetails,
  selectedStore,
  showBackstockAccounting = false,
  onToggleBackstockAccounting,
}) => {
  const [openInfo, setOpenInfo] = useState(false);
  const [expandAllRows, setExpandAllRows] = useState(false);
  const [expandAllStoreKey, setExpandAllStoreKey] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [auditsByDate, setAuditsByDate] = useState<Record<string, any[]>>({});
  const [loadingAuditsByDate, setLoadingAuditsByDate] = useState<Record<string, boolean>>({});
  const [isPinnedToViewport, setIsPinnedToViewport] = useState(false);
  const [pinnedViewportHeight, setPinnedViewportHeight] = useState<number | null>(null);
  const hideStockColumns = !showBackstockAccounting;
  const [dateSortDirection, setDateSortDirection] = useState<'asc' | 'desc'>('desc');
  const tableShellRef = useRef<HTMLDivElement | null>(null);
  const requestedAuditDatesRef = useRef<Map<string, number>>(new Map());
  const loadedAuditDatesRef = useRef<Set<string>>(new Set());
  const requestVersionRef = useRef(0);
  const expandAllLoadSequenceRef = useRef(0);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { startDate, endDate } = useFilterValues();
  const { data: auditSummariesResponse, isLoading } = useStoreAuditSummaries(id || '', startDate, endDate);
  const {
    data: backstockLedger,
  } = useStoreBackstockLedger(id || '', startDate, endDate, Boolean(id && startDate && endDate));
  const { data: allStores, isLoading: isAllStoresLoading } = useStoresSummaryStoresOnly();
  const storeDetails = allStores?.find(item => item.store.id === id)?.store as any;
  const currentStoreKey = React.useMemo(() => getAuditStoreKey(id), [id]);
  // Fetch audits complete through date data
  const { data: auditsCompleteData } = useAuditsCompleteThroughDate(id || '');

  const borderColumns = [0, 1, 6, 11, 14, 17, 18];
  const isBothConnected = storeDetails?.pos_type && storeDetails?.pos_type !== "n/a" && storeDetails?.lotto_authorized;
  const isBothNotConnected = (!storeDetails?.pos_type || storeDetails?.pos_type === "n/a") && (!storeDetails?.lotto_authorized);
  const isLottoConnectedOnly = storeDetails?.lotto_authorized && (!storeDetails?.pos_type || storeDetails?.pos_type === "n/a");
  const isPOSConnectedOnly = storeDetails?.pos_type && storeDetails?.pos_type !== "n/a" && !storeDetails?.lotto_authorized;
  const showNetVariance = !showBackstockAccounting;

  const getColumnConfig = () => {
    if (isBothConnected) return COLUMN_CONFIGS.BothConnected;
    if (isLottoConnectedOnly) return COLUMN_CONFIGS.LottoConnectedOnly;
    if (isPOSConnectedOnly) return COLUMN_CONFIGS.POSConnectedOnly;
    return COLUMN_CONFIGS.BothNotConnected;
  };

  const baseColumnConfig: readonly string[] = getColumnConfig();
  const movementColumnConfig = React.useMemo(() => {
    if (!showBackstockAccounting) {
      return baseColumnConfig;
    }

    if (isBothConnected || isPOSConnectedOnly) {
      return baseColumnConfig.slice(0, 7);
    }

    if (isLottoConnectedOnly) {
      return baseColumnConfig.slice(0, 8);
    }

    return baseColumnConfig;
  }, [baseColumnConfig, isBothConnected, isLottoConnectedOnly, isPOSConnectedOnly, showBackstockAccounting]);

  const columnConfig = React.useMemo(() => {
    const withBackstockAccounting = showBackstockAccounting
      ? [
        ...BACKSTOCK_ACCOUNTING_COLUMNS,
        ...movementColumnConfig.slice(0, 2),
        'From Backstock',
        'To Backstock',
        'Sold Out',
        ...movementColumnConfig.slice(2, 3),
        ...movementColumnConfig.slice(4),
      ]
      : [...movementColumnConfig];

    if (!hideStockColumns) {
      return withBackstockAccounting;
    }

    return withBackstockAccounting.filter((column) => !STOCK_COLUMN_LABELS.has(column));
  }, [hideStockColumns, movementColumnConfig, showBackstockAccounting]);

  // Final top-level Net Variance column index: Date (0) + second-row columns.
  const reconciliationColumnIndex = 1 + columnConfig.length;
  const totalColumnCount = reconciliationColumnIndex + (showNetVariance ? 1 : 0);
  const auditTableColStyles = React.useMemo(
    () => getAuditTableColStyles(columnConfig, showNetVariance, showBackstockAccounting),
    [columnConfig, showBackstockAccounting, showNetVariance]
  );
  const visibleStockColumnCount = hideStockColumns
    ? 0
    : showBackstockAccounting
      ? COUNTER_MOVEMENT_STOCK_COLUMN_COUNT
      : STOCK_COLUMN_LABELS.size;
  const backstockColumnOffset = showBackstockAccounting
    ? BACKSTOCK_ACCOUNTING_COLUMNS.length + visibleStockColumnCount - 4
    : visibleStockColumnCount - STOCK_COLUMN_LABELS.size;

  // Conditional borderColumns based on the 4 cases
  const baseConditionalBorderColumns = isBothConnected
    ? [0, 7, 10, 14, 15]
    : isBothNotConnected
      ? [0, 6, 8, 9]
      : isPOSConnectedOnly
        ? [0, 7, 10, 14, 15]
        : [0, 6, 8, 10, 11]; // LottoConnectedOnly
  const conditionalBorderColumns = baseConditionalBorderColumns.map((columnIndex) => (
    columnIndex > 4 ? columnIndex + backstockColumnOffset : columnIndex
  ));
  const movementConditionalBorderColumns = showBackstockAccounting
    ? Array.from(new Set([...conditionalBorderColumns, BACKSTOCK_ACCOUNTING_COLUMNS.length]))
    : conditionalBorderColumns;

  const ledgerByDate = React.useMemo(() => {
    const byDate: Record<string, BackstockLedgerRow> = {};
    (backstockLedger?.daily ?? []).forEach((row) => {
      if (row.report_date) {
        byDate[row.report_date] = row;
      }
    });
    return byDate;
  }, [backstockLedger]);

  // Filter and sort audit summaries at the top level
  const processedAuditSummaries = React.useMemo(() => {
    const auditSummaries = auditSummariesResponse?.dailySummaries || [];

    if (!auditSummaries || auditSummaries.length === 0) {
      return [];
    }

    // Merge lazy-loaded per-audit details by date.
    const summariesWithAudits = auditSummaries.map((row: any) => {
      const reportDate = row?.report_date;
      const injectedAudits = reportDate ? auditsByDate[reportDate] : undefined;
      if (injectedAudits) {
        return { ...row, audits: injectedAudits };
      }
      return row;
    });

    // Filter out rows with no activity
    const filteredAuditSummaries = summariesWithAudits.filter((row: any) => {
      // Get component values directly from response data
      const posScratcherQuantity = Number(row?.pos_summary?.scratcher_quantity_sold ?? 0);
      const totalSold = Number(row?.audit_summary?.total_sold ?? 0);
      const posScratcherSales = Number(row?.pos_summary?.scratcher_sales ?? 0);
      const revenue = Number(row?.audit_summary?.revenue ?? 0);
      const posLottoSales = Number(row?.pos_summary?.lotto_sales ?? 0);
      const lottoSales = Number(row?.lotto_summary?.sales ?? 0);
      const lottoPayoutsTotal = Number(row?.lotto_summary?.payouts_total ?? 0);
      const freeTicketsAmount = Number(row?.lotto_summary?.scratcher_free_tickets_paid_amount ?? 0);
      const posPayout = Number(row?.pos_summary?.payout ?? 0);
      const auditCount = Number(row?.audit_summary?.audit_count ?? 0);
      const movedToBackstockCount = Number(row?.audit_summary?.moved_to_backstock_count ?? 0);
      const backstockClosingPacks = Number(ledgerByDate[row?.report_date]?.closing_packs ?? 0);

      // Keep rows with activity OR explicit missing-audit marker from backend.
      const hasActivity = posScratcherQuantity !== 0 || totalSold !== 0 || posScratcherSales !== 0 || revenue !== 0 || posLottoSales !== 0 || lottoSales !== 0 || lottoPayoutsTotal !== 0 || freeTicketsAmount !== 0 || posPayout !== 0 || auditCount !== 0 || movedToBackstockCount !== 0 || backstockClosingPacks !== 0;
      const isMissingAuditRow = Boolean(row?.audit_missing);

      return hasActivity || isMissingAuditRow;
    });

    const sortedAuditSummaries = [...filteredAuditSummaries].sort((a: any, b: any) => {
      const dateA = new Date(a?.report_date);
      const dateB = new Date(b?.report_date);
      return dateSortDirection === 'asc'
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    });

    return sortedAuditSummaries;
  }, [auditSummariesResponse?.dailySummaries, auditsByDate, dateSortDirection, ledgerByDate]);


  const expandableAuditRows = React.useMemo<ExpandableAuditRow[]>(() => {
    return processedAuditSummaries.reduce<ExpandableAuditRow[]>((rows, row) => {
      const reportDate = row?.report_date;
      if (reportDate && getAuditRowAuditCount(row) > 0) {
        rows.push({ report_date: reportDate });
      }
      return rows;
    }, []);
  }, [processedAuditSummaries]);

  const expandedRowKeySet = React.useMemo(() => new Set(expandedRowKeys), [expandedRowKeys]);

  const overallTotalsRow = auditSummariesResponse?.overallTotals ?? null;
  const auditTableBodyRowCount = processedAuditSummaries.length;
  const hasTableRows = processedAuditSummaries.length > 0;
  const canUsePinnedViewportMode = !isLoading && !isAllStoresLoading && hasTableRows;
  const auditTableContainerClassName = [
    'audit-table__container',
    (isLoading || isAllStoresLoading || !hasTableRows) ? 'audit-table__container--compact' : '',
    canUsePinnedViewportMode && isPinnedToViewport ? 'audit-table__container--pinned' : '',
    showBackstockAccounting ? 'audit-table__container--movement-view' : ''
  ].filter(Boolean).join(' ');
  const auditTableShellClassName = [
    'audit-table__shell',
    canUsePinnedViewportMode && isPinnedToViewport ? 'audit-table__shell--pinned' : ''
  ].filter(Boolean).join(' ');

  const loadAuditsForDate = useCallback(async (reportDate?: string | null) => {
    if (!id || !reportDate || loadedAuditDatesRef.current.has(reportDate) || requestedAuditDatesRef.current.has(reportDate)) {
      return;
    }

    const requestVersion = requestVersionRef.current;
    requestedAuditDatesRef.current.set(reportDate, requestVersion);
    setLoadingAuditsByDate((prev) => ({ ...prev, [reportDate]: true }));

    try {
      const audits = await getStoreReconciliationAuditsByDate(id, reportDate);
      if (requestVersionRef.current !== requestVersion) {
        return;
      }
      const sortedAudits = [...audits].sort((a: any, b: any) => {
        const aTime = new Date(a?.report_datetime || a?.audit_date || a?.audit_created_at || 0).getTime();
        const bTime = new Date(b?.report_datetime || b?.audit_date || b?.audit_created_at || 0).getTime();
        return aTime - bTime;
      });

      loadedAuditDatesRef.current.add(reportDate);
      setAuditsByDate((prev) => ({ ...prev, [reportDate]: sortedAudits }));
    } finally {
      if (requestedAuditDatesRef.current.get(reportDate) === requestVersion) {
        requestedAuditDatesRef.current.delete(reportDate);
      }

      if (requestVersionRef.current !== requestVersion) {
        return;
      }

      setLoadingAuditsByDate((prev) => ({ ...prev, [reportDate]: false }));
    }
  }, [id]);

  const loadAuditsForRowsInOrder = useCallback(async (rows: ExpandableAuditRow[]) => {
    const loadSequence = ++expandAllLoadSequenceRef.current;
    const reportDates = rows.reduce<string[]>((dates, row) => {
      const reportDate = row?.report_date;
      if (reportDate && !dates.includes(reportDate)) {
        dates.push(reportDate);
      }
      return dates;
    }, []);

    for (let index = 0; index < reportDates.length; index += EXPAND_ALL_AUDIT_DETAILS_CONCURRENCY) {
      if (expandAllLoadSequenceRef.current !== loadSequence) {
        return;
      }

      const batch = reportDates.slice(index, index + EXPAND_ALL_AUDIT_DETAILS_CONCURRENCY);
      await Promise.all(batch.map((reportDate) => loadAuditsForDate(reportDate)));
    }
  }, [loadAuditsForDate]);

  const handleExpandClick = (rowIdx: number) => {
    const row = processedAuditSummaries[rowIdx];
    const isExpandable = getAuditRowAuditCount(row) > 0;

    if (!isExpandable) {
      return;
    }

    const reportDate = row?.report_date;
    if (!reportDate) {
      return;
    }

    const rowKey = getAuditRowExpandKey(id, reportDate);
    const isCurrentlyExpanded = expandedRowKeySet.has(rowKey);

    setExpandedRowKeys((prev) => {
      const nextExpandedKeys = prev.includes(rowKey)
        ? prev.filter((key) => key !== rowKey)
        : [...prev, rowKey];
      const areAllRowsExpanded = expandableAuditRows.length > 0 &&
        expandableAuditRows.every(({ report_date: expandableReportDate }) => {
          return expandableReportDate
            ? nextExpandedKeys.includes(getAuditRowExpandKey(id, expandableReportDate))
            : false;
        });

      setExpandAllRows(areAllRowsExpanded);
      return nextExpandedKeys;
    });

    if (!isCurrentlyExpanded) {
      void loadAuditsForDate(reportDate);
    }
  }

  const handleExpandAllChange = (checked: boolean) => {
    const shouldExpandAll = checked && expandableAuditRows.length > 0;

    setExpandAllRows(shouldExpandAll);
    if (shouldExpandAll) {
      setExpandAllStoreKey(currentStoreKey);
      setExpandedRowKeys(
        expandableAuditRows
          .map(({ report_date }) => getAuditRowExpandKey(id, report_date))
      );
      return;
    }

    expandAllLoadSequenceRef.current += 1;
    setExpandAllStoreKey(null);
    setExpandedRowKeys([]);
  };

  const handleInfoClick = () => {
    setOpenInfo(true);
  }

  const handleAuditPackMetricClick = useCallback((
    event: React.MouseEvent<HTMLButtonElement>,
    row: AuditRow,
    metric: 'opening' | 'activated' | 'closing' | 'counter_sold_out'
  ) => {
    event.stopPropagation();

    if (!id || !row.report_date) {
      return;
    }

    navigate(buildCurrentInventoryStatusPath(id, {
      metric,
      report_date: row.report_date,
    }));
  }, [id, navigate]);

  const handleBackstockPackMetricClick = useCallback((
    event: React.MouseEvent<HTMLButtonElement>,
    row: AuditRow,
    metric: BackstockPackMetric
  ) => {
    event.stopPropagation();

    if (!id || !row.report_date) {
      return;
    }

    const packScopeMetric = `backstock_${metric}` as AuditReportPackScopeMetric;
    navigate(buildCurrentInventoryStatusPath(id, {
      metric: packScopeMetric,
      report_date: row.report_date,
      start_date: startDate ?? undefined,
      end_date: endDate ?? undefined,
    }));
  }, [endDate, id, navigate, startDate]);

  const handleOverallBackstockPackMetricClick = useCallback((
    event: React.MouseEvent<HTMLButtonElement>,
    metric: BackstockPackMetric
  ) => {
    event.stopPropagation();

    if (!id || !startDate || !endDate) {
      return;
    }

    const packScopeMetric = `backstock_${metric}` as AuditReportPackScopeMetric;
    navigate(buildCurrentInventoryStatusPath(id, {
      metric: packScopeMetric,
      start_date: startDate,
      end_date: endDate,
    }));
  }, [endDate, id, navigate, startDate]);

  const handleOverallAuditPackMetricClick = useCallback((
    event: React.MouseEvent<HTMLButtonElement>,
    metric: 'opening' | 'activated' | 'closing' | 'counter_sold_out'
  ) => {
    event.stopPropagation();

    if (!id || !startDate || !endDate) {
      return;
    }

    navigate(buildCurrentInventoryStatusPath(id, {
      metric,
      start_date: startDate,
      end_date: endDate,
    }));
  }, [endDate, id, navigate, startDate]);


  useEffect(() => {
    requestVersionRef.current += 1;
    requestedAuditDatesRef.current.clear();
    loadedAuditDatesRef.current.clear();
    expandAllLoadSequenceRef.current += 1;
    setExpandAllRows(false);
    setExpandAllStoreKey(null);
    setExpandedRowKeys([]);
    setAuditsByDate({});
    setLoadingAuditsByDate({});
  }, [id, startDate, endDate]);

  useEffect(() => {
    if (!expandAllRows || expandAllStoreKey !== currentStoreKey) {
      return;
    }

    setExpandedRowKeys(expandableAuditRows.map(({ report_date }) => getAuditRowExpandKey(id, report_date)));
    void loadAuditsForRowsInOrder(expandableAuditRows);
  }, [expandAllRows, expandAllStoreKey, currentStoreKey, expandableAuditRows, id, loadAuditsForRowsInOrder]);

  useEffect(() => {
    if (!canUsePinnedViewportMode) {
      setIsPinnedToViewport(false);
      setPinnedViewportHeight(null);
      return;
    }

    const getNavbarHeight = () => {
      const dashboardLayout = document.querySelector('.dashboard-layout');
      const cssValue = dashboardLayout
        ? window.getComputedStyle(dashboardLayout).getPropertyValue('--dashboard-appbar-height')
        : '';
      const parsedHeight = Number.parseFloat(cssValue);

      return Number.isFinite(parsedHeight) ? parsedHeight : 62;
    };

    const getPinnedOffset = () => {
      const shellElement = tableShellRef.current;
      const cssValue = shellElement
        ? window.getComputedStyle(shellElement).getPropertyValue('--audit-table-pinned-offset')
        : '';
      const parsedOffset = Number.parseFloat(cssValue);

      return Number.isFinite(parsedOffset) ? parsedOffset : 24;
    };

    const updatePinnedMetrics = () => {
      const shellElement = tableShellRef.current;

      if (!shellElement) {
        return;
      }

      const navbarHeight = getNavbarHeight();
      const pinnedOffset = getPinnedOffset();
      const pinStartTop = navbarHeight + pinnedOffset;
      const shellRect = shellElement.getBoundingClientRect();
      const shouldPinTable = shellRect.top <= pinStartTop;
      const nextPinnedHeight = Math.max(window.innerHeight - pinStartTop - 16, 320);

      setIsPinnedToViewport(shouldPinTable);
      setPinnedViewportHeight(nextPinnedHeight);
    };

    updatePinnedMetrics();
    let frameId = 0;

    const handleScroll = () => {
      if (frameId) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        updatePinnedMetrics();
      });
    };

    const handleResize = () => {
      updatePinnedMetrics();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [canUsePinnedViewportMode]);

  return (
    <div
      ref={tableShellRef}
      className={auditTableShellClassName}
      style={
        canUsePinnedViewportMode && pinnedViewportHeight
          ? { ['--audit-table-pinned-height' as string]: `${pinnedViewportHeight}px` }
          : undefined
      }
    >
      <TableContainer
        component={Paper}
        sx={tableContainerStyle}
        className={auditTableContainerClassName}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: '100%' }}>
          <colgroup>
            {auditTableColStyles.map((style, index) => (
              <col key={index} style={{ width: style.width, minWidth: style.minWidth, maxWidth: style.maxWidth }} />
            ))}
          </colgroup>
          <AuditTableHeader
            onInfoClick={handleInfoClick}
            borderColumns={borderColumns}
            selectedStore={storeDetails}
            conditionalBorderColumns={movementConditionalBorderColumns}
            columnConfig={columnConfig}
            reconciliationColumnIndex={reconciliationColumnIndex}
            showNetVariance={showNetVariance}
            isExpandAllChecked={expandAllRows}
            onExpandAllChange={handleExpandAllChange}
            hideStockColumns={hideStockColumns}
            showBackstockAccounting={showBackstockAccounting}
            onToggleBackstockAccounting={onToggleBackstockAccounting}
            dateSortDirection={dateSortDirection}
            onDateSortChange={() => setDateSortDirection((current) => current === 'asc' ? 'desc' : 'asc')}
          />

          <TableBody>
            {isLoading || isAllStoresLoading ? (
              <TableLoading colSpan={totalColumnCount} />
            ) : processedAuditSummaries.length > 0 ? (
              <>
                {processedAuditSummaries.map((row: any, rowIdx: number) => {
                  const reportDate = row?.report_date;
                  const rowKey = getAuditRowExpandKey(id, reportDate);
                  const ledgerRow = ledgerByDate[reportDate || ''];
                  const isExpandable = getAuditRowAuditCount(row) > 0;
                  const isExpanded = isExpandable && expandedRowKeySet.has(rowKey);
                  const hasLoadedAudits = Boolean(
                    reportDate && Object.prototype.hasOwnProperty.call(auditsByDate, reportDate)
                  );
                  const isLoadingAudits = Boolean(
                    isExpanded &&
                    isExpandable &&
                    reportDate &&
                    (loadingAuditsByDate[reportDate] || !hasLoadedAudits)
                  );

                  return (<AuditTableRow
                    key={rowKey}
                    row={row}
                    rowIdx={rowIdx}
                    isExpanded={isExpanded}
                    isExpandable={isExpandable}
                    onExpandClick={handleExpandClick}
                    auditSummariesLength={auditTableBodyRowCount}
                    borderColumns={borderColumns}
                    storeId={id || ''}
                    onAuditDetails={handleAuditDetails}
                    selectedStore={storeDetails}
                    effectiveDate={auditsCompleteData?.data?.effective_date}
                    conditionalBorderColumns={movementConditionalBorderColumns}
                    reconciliationColumnIndex={reconciliationColumnIndex}
                    showNetVariance={showNetVariance}
                    isLoadingAudits={isLoadingAudits}
                    hideStockColumns={hideStockColumns}
                    showBackstockAccounting={showBackstockAccounting}
                    backstockLedgerRow={ledgerRow}
                    backstockColumnOffset={backstockColumnOffset}
                    onAuditPackMetricClick={handleAuditPackMetricClick}
                    onBackstockPackMetricClick={handleBackstockPackMetricClick}
                  />);
                })}
              </>
            ) : (
              <AuditTableEmpty colSpan={totalColumnCount} />
            )}
          </TableBody>
          {processedAuditSummaries.length > 0 && overallTotalsRow && !(isAllStoresLoading) && (
            <TableFooter className="audit-table__footer">
              <AuditTableTotalRow
                totalRow={overallTotalsRow}
                selectedStore={storeDetails}
                conditionalBorderColumns={movementConditionalBorderColumns}
                rowIdx={0}
                totalRows={1}
                hideStockColumns={hideStockColumns}
                showBackstockAccounting={showBackstockAccounting}
                backstockLedgerRow={backstockLedger?.overall}
                backstockColumnOffset={backstockColumnOffset}
                showNetVariance={showNetVariance}
                onBackstockPackMetricClick={handleOverallBackstockPackMetricClick}
                onAuditPackMetricClick={handleOverallAuditPackMetricClick}
              />
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      {openInfo && <PayoutInfoPopup open={openInfo} handleClose={() => setOpenInfo(false)} state="State" storeKey="store" />}
    </div>
  );
});

export default AuditTable; 
